"use client";

import { artistLinkRel } from "@/lib/seo-enrichment";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslations, useLocale } from "next-intl";
import { Link, useRouter } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import Image from "next/image";
import { MapPin, ExternalLink, Calendar, X } from "lucide-react";
import {
  Festival,
  FestivalDay,
  FestivalAttraction,
  FestivalActivity,
  FestivalLineupEntry,
  CityConfig,
  Event,
  isActivity,
} from "@/lib/types";
import { isOwnImage } from "@/lib/utils";
import { ArtistExternalLinks } from "../artist-external-links";
import { LineupEntryModal } from "../admin/lineup-entry-modal";
import { AddLineupEntryModal } from "../admin/add-lineup-entry-modal";
import { formatDateRange, formatTime } from "@/lib/utils";
import { useSpotifyPlayer } from "../spotify/spotify-player-context";
import { useJukeboxReopen } from "../spotify/use-jukebox-reopen";
import { useSetSpotifyCitySlug } from "../spotify/use-set-city-slug";
import { AuthProvider, useAuth } from "../auth/auth-context";
import { EventAdminModal } from "../admin/event-admin-modal";
import { FestivalAdminModal } from "../admin/festival-admin-modal";
import { SubscribeButton } from "../subscribe-button";
import { UserMenu } from "../auth/user-menu";
import { LanguageSwitcher } from "../language-switcher";
import { SearchButton } from "../search/search-button";
import { User } from "@supabase/supabase-js";
import { GenreJukeboxCta } from "../events/genre-jukebox-cta";
import { ExportGridButton } from "./schedule/export-grid-button";
import { GenreJukeboxModal, JukeboxEvent } from "../events/genre-jukebox-modal";
import { ScheduleView, StageFilter, STAGE_FILTER_THRESHOLD } from "./schedule/schedule-view";
import { useScheduleData } from "./schedule/use-schedule-data";
import { ScheduleProvider, useScheduleBuilder } from "./schedule/schedule-context";

interface FestivalPageClientProps {
  festival: Festival;
  days: FestivalDay[];
  city: CityConfig;
  blockedSpotifyIds: string[];
  initialUser?: User | null;
  initialDate?: string;
  stageVenues?: Record<string, { slug: string; citySlug: string }>;
}

// --- Day tabs (desktop) + dropdown (mobile) ---

import { Select } from "../select";
import { PlayIcon } from "@/components/spotify/play-icon";

function formatDayLabel(date: string) {
  if (date === "TBA") return "TBA";
  const d = new Date(date + "T00:00:00");
  return d.toLocaleDateString("en-US", { weekday: "short", day: "numeric", month: "short" }).toUpperCase();
}

function formatDayLong(date: string, locale: string) {
  if (date === "TBA") return "TBA";
  return new Date(date + "T00:00:00").toLocaleDateString(locale === "de-CH" ? "de-CH" : "en-US", { weekday: "long", month: "long", day: "numeric" });
}

function DayTabs({
  days,
  selectedDate,
  onSelect,
}: {
  days: FestivalDay[];
  selectedDate: string;
  onSelect: (date: string) => void;
}) {
  const dayOptions = useMemo(
    () => days.map((day) => ({ label: formatDayLabel(day.date), value: day.date })),
    [days]
  );

  return (
    <>
      {/* Mobile: styled dropdown */}
      <div className="md:hidden">
        <Select
          value={selectedDate}
          onChange={onSelect}
          options={dayOptions}
          size="sm"
        />
      </div>

      {/* Desktop: button tabs */}
      <div className="hidden md:flex flex-wrap gap-1.5">
        {days.map((day) => {
          const isActive = day.date === selectedDate;
          return (
            <button
              key={day.date}
              onClick={() => onSelect(day.date)}
              className={`rounded-lg px-4 py-2 font-mono text-xs font-bold tracking-wider transition-all cursor-pointer ${
                isActive
                  ? "bg-acid text-black"
                  : "border border-neutral-700 text-neutral-400 hover:border-neutral-500 hover:text-white"
              }`}
            >
              {formatDayLabel(day.date)}
            </button>
          );
        })}
      </div>
    </>
  );
}

type ViewMode = "list" | "grid" | "my-grid";

function ViewModeToggle({
  scheduleEligible,
  viewMode,
  onSetViewMode,
  savedSetCount,
}: {
  scheduleEligible: boolean;
  viewMode: ViewMode;
  onSetViewMode: (mode: ViewMode) => void;
  savedSetCount: number;
}) {
  if (!scheduleEligible) return null;

  return (
    <div className="flex items-center gap-1 rounded-lg border border-neutral-800 p-0.5 ml-auto md:ml-auto">
      <button
        onClick={() => onSetViewMode("list")}
        className={`cursor-pointer rounded-md px-3 py-1.5 font-mono text-[10px] font-bold tracking-wider transition-all ${
          viewMode === "list"
            ? "bg-neutral-800 text-white"
            : "text-neutral-500 hover:text-neutral-300"
        }`}
      >
        LIST
      </button>
      <button
        onClick={() => onSetViewMode("grid")}
        className={`cursor-pointer rounded-md px-3 py-1.5 font-mono text-[10px] font-bold tracking-wider transition-all ${
          viewMode === "grid"
            ? "bg-neutral-800 text-white"
            : "text-neutral-500 hover:text-neutral-300"
        }`}
      >
        GRID
      </button>
      {savedSetCount > 0 && (
        <button
          onClick={() => onSetViewMode("my-grid")}
          className={`cursor-pointer rounded-md px-3 py-1.5 font-mono text-[10px] font-bold tracking-wider transition-all ${
            viewMode === "my-grid"
              ? "bg-acid text-black"
              : "text-neutral-500 hover:text-neutral-300"
          }`}
        >
          MY GRID ({savedSetCount})
        </button>
      )}
    </div>
  );
}

// --- Spotify play button ---

function SpotifyPlayButton({
  spotifyId,
  artistName,
}: {
  spotifyId: string;
  artistName: string;
}) {
  const { play, artistId: activeArtistId } = useSpotifyPlayer();
  const isPlaying = spotifyId === activeArtistId;

  return (
    <button
      onClick={() => play(spotifyId, artistName)}
      title={`Preview ${artistName}`}
      className={`flex h-10 w-10 shrink-0 cursor-pointer items-center justify-center rounded-full transition-all ${
        isPlaying
          ? "bg-[#1DB954]/20 text-[#1DB954] shadow-[0_0_8px_rgba(29,185,84,0.3)]"
          : "text-neutral-400 hover:text-[#1DB954] hover:bg-neutral-700"
      }`}
    >
      <PlayIcon className="h-4 w-4" />
    </button>
  );
}

// --- Artist row ---

function FestivalArtistRow({
  attraction,
  blockedSpotifyIds,
  showTime,
  eventId,
}: {
  attraction: FestivalAttraction;
  blockedSpotifyIds: string[];
  showTime?: boolean;
  eventId?: number;
}) {
  const { isAdmin } = useAuth();
  const [showEditModal, setShowEditModal] = useState(false);
  const a = attraction;
  const hasSpotify = a.spotify_id && !blockedSpotifyIds.includes(a.spotify_id);
  const hasLinks = a.external_links && Object.keys(a.external_links).length > 0;
  const displayGenres = [...new Set(a.genres.filter(Boolean))].slice(0, 2);

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div
        className={`flex gap-2.5 ${hasLinks ? "items-start" : "items-center"}`}
      >
        {/* Set time */}
        {showTime && a.set_time && (
          <span className="w-12 shrink-0 pt-0.5 font-mono text-xs text-neutral-500">
            {formatTime(a.set_time)}
          </span>
        )}

        {/* Artist/event image */}
        <Link
          href={a.child_event ? `/${a.child_event.city_slug}/event/${a.child_event.slug}` : `/artist/${a.slug}`}
          rel={a.child_event ? "nofollow" : artistLinkRel(a)}
          prefetch={false}
          className={`relative h-11 w-11 shrink-0 overflow-hidden rounded-full border border-neutral-700 ${hasLinks ? "mt-0.5" : ""}`}
        >
          {a.image_url && isOwnImage(a.image_url) ? (
            <Image
              src={a.image_url}
              alt={a.name}
              fill
              className="object-cover"
              sizes="44px"
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center bg-neutral-800 font-mono text-xs text-neutral-400">
              {a.name.charAt(0).toUpperCase()}
            </div>
          )}
        </Link>

        {/* Name + links + genres */}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Link
              href={a.child_event ? `/${a.child_event.city_slug}/event/${a.child_event.slug}` : `/artist/${a.slug}`}
              rel={a.child_event ? "nofollow" : artistLinkRel(a)}
              prefetch={false}
              className="truncate font-mono text-sm text-neutral-200 transition-colors hover:text-acid"
            >
              {a.name}
            </Link>
            {displayGenres.length > 0 && (
              <div className="hidden sm:flex flex-wrap items-center gap-1">
                {displayGenres.map((g, i) => (
                  <span
                    key={`${g}-${i}`}
                    className="whitespace-nowrap rounded-full border border-acid/20 px-1.5 py-0.5 font-mono text-[9px] text-acid/70"
                  >
                    {g.toUpperCase()}
                  </span>
                ))}
              </div>
            )}
          </div>
          {hasLinks && (
            <div className="mt-1 flex flex-wrap items-center gap-1.5">
              <ArtistExternalLinks links={a.external_links} size="sm" />
            </div>
          )}
        </div>

        {/* Spotify + subscribe */}
        {hasSpotify && (
          <SpotifyPlayButton
            spotifyId={a.spotify_id as string}
            artistName={a.name}
          />
        )}
        <SubscribeButton
          type="artist"
          targetId={a.id}
          targetName={a.name}
          targetImage={a.image_url}
          size="sm"
        />
        {isAdmin && eventId && (
          <>
            <button
              onClick={() => setShowEditModal(true)}
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-neutral-600 transition-all hover:text-acid cursor-pointer"
              title="Edit lineup entry"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                <path d="m15 5 4 4" />
              </svg>
            </button>
            {showEditModal && (
              <LineupEntryModal
                eventId={eventId}
                attraction={a}
                onClose={() => setShowEditModal(false)}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}

// --- Activity row (non-artist lineup entry) ---

function FestivalActivityRow({
  activity,
  showTime,
}: {
  activity: FestivalActivity;
  showTime?: boolean;
}) {
  return (
    <div className="py-3 first:pt-0 last:pb-0">
      <div className="flex gap-2.5 items-center">
        {showTime && activity.set_time && (
          <span className="w-12 shrink-0 pt-0.5 font-mono text-xs text-neutral-500">
            {formatTime(activity.set_time)}
          </span>
        )}
        <div className="relative h-11 w-11 shrink-0 overflow-hidden rounded-full border border-dashed border-neutral-700/50">
          <div className="flex h-full w-full items-center justify-center bg-neutral-800/50 font-mono text-xs text-neutral-500">
            &#9733;
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <span className="font-mono text-sm text-neutral-400 italic">
            {activity.activity_name}
          </span>
        </div>
      </div>
    </div>
  );
}

// --- Day content: flat list or grouped by stage ---

function DayContent({
  day,
  blockedSpotifyIds,
  visibleStages,
}: {
  day: FestivalDay;
  blockedSpotifyIds: string[];
  visibleStages?: Set<string>;
}) {
  const hasStages = day.attractions.some((a) => a.stage != null);
  const filteredAttractions = visibleStages
    ? day.attractions.filter((a) => !a.stage || visibleStages.has(a.stage))
    : day.attractions;

  function renderLineupEntry(entry: FestivalLineupEntry, showTime?: boolean) {
    if (isActivity(entry)) {
      return (
        <FestivalActivityRow
          key={`activity-${entry.activity_name}-${entry.set_time}`}
          activity={entry}
          showTime={showTime}
        />
      );
    }
    return (
      <FestivalArtistRow
        key={entry.id}
        attraction={entry}
        blockedSpotifyIds={blockedSpotifyIds}
        showTime={showTime}
        eventId={day.event_id}
      />
    );
  }

  if (!hasStages) {
    // Flat list sorted by sort_order (billing order)
    return (
      <div className="divide-y divide-neutral-800/50">
        {filteredAttractions.map((a) => renderLineupEntry(a))}
      </div>
    );
  }

  // Group by stage
  const stageMap = new Map<string, FestivalLineupEntry[]>();
  const noStage: FestivalLineupEntry[] = [];

  for (const a of filteredAttractions) {
    if (a.stage) {
      const list = stageMap.get(a.stage) || [];
      list.push(a);
      stageMap.set(a.stage, list);
    } else {
      noStage.push(a);
    }
  }

  // Sort each stage by set_time, then sort_order
  for (const list of stageMap.values()) {
    list.sort((a, b) => {
      if (a.set_time && b.set_time) return a.set_time.localeCompare(b.set_time);
      return (a.sort_order ?? 999) - (b.sort_order ?? 999);
    });
  }

  return (
    <div className="space-y-6">
      {[...stageMap.entries()].map(([stage, entries]) => (
        <div key={stage}>
          <div className="mb-3 flex items-center gap-3">
            <div className="h-px flex-1 bg-neutral-800" />
            <span className="font-mono text-[10px] font-bold tracking-widest text-neutral-500">
              {stage.toUpperCase()}
            </span>
            <div className="h-px flex-1 bg-neutral-800" />
          </div>
          <div className="divide-y divide-neutral-800/50">
            {entries.map((a) => renderLineupEntry(a, true))}
          </div>
        </div>
      ))}
      {noStage.length > 0 && (
        <div className="divide-y divide-neutral-800/50">
          {noStage.map((a) => renderLineupEntry(a))}
        </div>
      )}
    </div>
  );
}

// --- Main festival page ---

/** Build a minimal Event object from a FestivalDay so we can reuse EventAdminModal. */
function dayToEvent(day: FestivalDay, festival: Festival): Event {
  return {
    id: day.event_id,
    tm_id: null,
    name: `${festival.name} — ${formatDayLabel(day.date)}`,
    slug: "",
    url: festival.url,
    image_url: festival.image_url,
    images: [],
    local_date: day.date,
    local_time: null,
    start_datetime: null,
    status: null,
    price_min: null,
    price_max: null,
    price_currency: null,
    info: null,
    please_note: null,
    image_focus: null,
    venue: festival.venue ?? { id: 0, tm_id: null, name: "", slug: "", address: null, city: null, latitude: 0, longitude: 0, url: null, image_url: null },
    attractions: day.attractions.filter((a): a is FestivalAttraction => !isActivity(a)).map((a) => ({
      id: a.id,
      tm_id: a.tm_id,
      name: a.name,
      slug: a.slug,
      image_url: a.image_url,
      genres: a.genres,
      spotify_id: a.spotify_id,
      external_links: a.external_links,
    })),
  };
}

function FestivalPageInner({
  festival,
  days,
  city,
  blockedSpotifyIds,
  initialDate,
  stageVenues,
}: Omit<FestivalPageClientProps, "initialUser">) {
  const t = useTranslations("Festival");
  const tJukebox = useTranslations("Jukebox");
  const tCommon = useTranslations("Common");
  const locale = useLocale();
  const router = useRouter();
  const { isAdmin } = useAuth();
  const { savedSetKeys } = useScheduleBuilder();
  useSetSpotifyCitySlug(city.slug);
  const { artistId: spotifyActiveId } = useSpotifyPlayer();
  const spotifyBarVisible = !!spotifyActiveId;

  const [selectedDate, setSelectedDate] = useState(initialDate ?? days[0]?.date ?? "");
  const [hasHistory, setHasHistory] = useState(false);
  const [adminModalOpen, setAdminModalOpen] = useState(false);
  const [addLineupOpen, setAddLineupOpen] = useState(false);
  const [festivalAdminOpen, setFestivalAdminOpen] = useState(false);
  const [jukeboxOpen, setJukeboxOpen] = useState(false);
  useJukeboxReopen(jukeboxOpen, () => setJukeboxOpen(true));
  const searchParams = useSearchParams();
  const anyDayGridEligible = useMemo(
    () => days.some((d) => d.attractions.filter((a) => a.stage && a.set_time).length >= 5),
    [days]
  );

  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const v = searchParams.get("view");
    if (v === "my-grid") return "my-grid";
    if (v === "grid" || v === "schedule") return "grid";
    return anyDayGridEligible ? "grid" : "list";
  });

  useEffect(() => {
    if (
      window.history.length > 1 &&
      document.referrer &&
      document.referrer.startsWith(window.location.origin)
    ) {
      setHasHistory(true);
    }
    // Clean up ?view= param from URL
    const params = new URLSearchParams(window.location.search);
    if (params.has("view")) {
      params.delete("view");
      const qs = params.toString();
      window.history.replaceState({}, "", `${window.location.pathname}${qs ? `?${qs}` : ""}`);
    }
  }, []);

  const selectedDay = days.find((d) => d.date === selectedDate);
  const totalArtists = days.reduce((sum, d) => sum + d.attractions.filter((a) => !isActivity(a)).length, 0);

  // Check if the selected day has enough schedule data to show the grid
  const scheduleEligible = useMemo(() => {
    if (!selectedDay) return false;
    const withSchedule = selectedDay.attractions.filter(
      (a) => a.stage && a.set_time
    );
    return withSchedule.length >= 5;
  }, [selectedDay]);

  // Stage filter state (for schedule grid view)
  const scheduleData = useScheduleData(selectedDay?.attractions ?? []);
  const showStageFilter = scheduleData !== null && scheduleData.stages.length > STAGE_FILTER_THRESHOLD;
  const [selectedStages, setSelectedStages] = useState<Set<string> | null>(null);

  // Reset filter when day changes
  useEffect(() => {
    setSelectedStages(null);
  }, [selectedDate]);

  const activeSelected = useMemo(() => {
    if (!scheduleData) return new Set<string>();
    return selectedStages ?? new Set(scheduleData.stages);
  }, [scheduleData, selectedStages]);

  const visibleStages = useMemo(() => {
    if (!scheduleData) return undefined;
    if (!showStageFilter) return undefined; // let ScheduleView use its default
    return scheduleData.stages.filter((s) => activeSelected.has(s));
  }, [scheduleData, showStageFilter, activeSelected]);

  const handleStageToggle = useCallback(
    (stage: string) => {
      if (!scheduleData) return;
      setSelectedStages((prev) => {
        const base = prev ?? new Set(scheduleData.stages);
        const next = new Set(base);
        if (next.has(stage)) next.delete(stage);
        else next.add(stage);
        return next;
      });
    },
    [scheduleData]
  );

  // Festival-wide jukebox: one entry per unique artist across all days
  const jukeboxPlaylist = useMemo<JukeboxEvent[]>(() => {
    const result: JukeboxEvent[] = [];
    const seenSpotifyIds = new Set<string>();
    for (const day of days) {
      const dayEvent = dayToEvent(day, festival);
      for (const a of day.attractions) {
        if (isActivity(a)) continue;
        if (!a.spotify_id || blockedSpotifyIds.includes(a.spotify_id)) continue;
        if (seenSpotifyIds.has(a.spotify_id)) continue;
        seenSpotifyIds.add(a.spotify_id);
        // Per-artist event: set time + stage as venue when no festival-level venue
        let event = dayEvent;
        const overrides: Partial<Event> = {};
        if (a.set_time) overrides.local_time = a.set_time;
        if (a.stage && (!festival.venue || !festival.venue.name)) {
          const sv = stageVenues?.[a.stage];
          overrides.venue = {
            ...dayEvent.venue,
            name: a.stage,
            slug: sv?.slug ?? dayEvent.venue.slug,
          };
        }
        if (Object.keys(overrides).length > 0) {
          event = { ...dayEvent, ...overrides };
        }
        result.push({
          event,
          artists: [{
            attractionId: a.id,
            name: a.child_event?.artist_name ?? a.name,
            slug: a.slug,
            imageUrl: a.image_url,
            spotifyId: a.spotify_id,
            genres: a.genres,
            externalLinks: a.external_links ?? undefined,
          }],
          subtitle: a.child_event ? a.name : undefined,
        });
      }
    }
    return result;
  }, [days, festival, blockedSpotifyIds, stageVenues]);

  const jukeboxArtistCount = useMemo(
    () => jukeboxPlaylist.reduce((sum, je) => sum + je.artists.length, 0),
    [jukeboxPlaylist]
  );

  // Restore jukebox session resume
  const sessionKey = `jukebox-festival-${city.slug}-${festival.slug}`;

  const jukeboxSessionRef = useRef<{ index: number } | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(sessionKey);
      if (raw) jukeboxSessionRef.current = JSON.parse(raw);
    } catch {}
  }, [sessionKey]);

  const getJukeboxResumeIndex = useCallback((): number => {
    return jukeboxSessionRef.current?.index ?? 0;
  }, []);

  const handleJukeboxClose = useCallback((lastIndex: number) => {
    const session = { index: lastIndex };
    jukeboxSessionRef.current = session;
    try {
      sessionStorage.setItem(sessionKey, JSON.stringify(session));
    } catch {}
    setJukeboxOpen(false);
  }, [sessionKey]);

  return (
    <>
      <div className="min-h-screen bg-dark font-sans text-white">
        <div
          className={`mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8 ${spotifyBarVisible ? "pb-[220px]" : "pb-6"}`}
        >
          {/* Header */}
          <div className="mb-6">
            {/* Top bar */}
            <div className="mb-4 flex items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Link
                  href="/"
                  className="flex items-center gap-2 transition-opacity hover:opacity-80"
                >
                  <div className="h-2.5 w-2.5 animate-pulse rounded-full bg-acid shadow-[0_0_10px_#D1F805]" />
                  <span className="font-mono text-xs tracking-[0.2em] text-neutral-500">
                    {tCommon("brand")}
                  </span>
                </Link>
                <span className="font-mono text-xs text-neutral-700">/</span>
                <Link
                  href={`/${city.slug}`}
                  className="font-mono text-xs text-neutral-500 transition-colors hover:text-acid"
                >
                  {city.name.toUpperCase()}
                </Link>
              </div>
              <div className="flex items-center gap-3">
                <SearchButton />
                <LanguageSwitcher />
                <UserMenu />
              </div>
            </div>

            {/* Festival title + meta */}
            <div className="flex items-start gap-4">
              {festival.image_url && (
                <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-lg border border-neutral-800">
                  <Image
                    src={festival.image_url}
                    alt={festival.name}
                    fill
                    className="object-cover"
                    sizes="64px"
                  />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <h1 className="flex items-center gap-2 font-mono text-2xl font-bold uppercase tracking-tight text-white sm:text-3xl">
                  {festival.name}
                  {isAdmin && (
                    <button
                      onClick={() => setFestivalAdminOpen(true)}
                      className="text-acid/70 transition-colors hover:text-acid cursor-pointer"
                      aria-label="Edit festival"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                        <path d="m15 5 4 4" />
                      </svg>
                    </button>
                  )}
                </h1>
                <div className="mt-1 flex flex-wrap items-center gap-3">
                  <span className="font-mono text-sm text-neutral-500">
                    {formatDateRange(
                      festival.start_date,
                      festival.end_date,
                      locale
                    )}
                  </span>
                  {festival.venue && (
                    <>
                      <span className="text-neutral-700">·</span>
                      <Link
                        href={`/${city.slug}/${festival.venue.slug}`}
                        className="flex items-center gap-1 font-mono text-sm text-neutral-400 transition-colors hover:text-acid"
                      >
                        <MapPin className="h-3 w-3" />
                        {festival.venue.name}
                      </Link>
                    </>
                  )}
                  {totalArtists > 0 && (
                    <>
                      <span className="text-neutral-700">·</span>
                      <span className="font-mono text-sm text-neutral-500">
                        {t("artists", { count: totalArtists })}
                      </span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {hasHistory && (
                  <button
                    onClick={() => router.back()}
                    className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-full border border-neutral-700/50 text-neutral-500 transition-all hover:border-neutral-600 hover:text-white"
                    aria-label="Go back"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Festival jukebox CTA */}
          {jukeboxPlaylist.length > 0 && (
            <div className="mb-6">
              <GenreJukeboxCta
                headline={tJukebox("ctaHeadlineFestival", { festival: festival.name })}
                artistCount={jukeboxArtistCount}
                cityName={festival.name}
                onClick={() => setJukeboxOpen(true)}
              />
            </div>
          )}

          {/* Day tabs + view toggle */}
          <div className="mb-6 flex items-center gap-2 md:gap-3 md:flex-wrap">
            {days.length > 1 && (
              <DayTabs
                days={days}
                selectedDate={selectedDate}
                onSelect={setSelectedDate}
              />
            )}
            <ViewModeToggle
              scheduleEligible={scheduleEligible}
              viewMode={viewMode}
              onSetViewMode={setViewMode}
              savedSetCount={savedSetKeys.size}
            />
          </div>

          {/* Schedule view — full width */}
          {(viewMode === "grid" || viewMode === "my-grid") && scheduleEligible && selectedDay ? (
            <div>
              {/* Lineup header + admin button */}
              <div className="mb-4 flex items-center justify-between">
                <h2 className="font-mono text-xs font-bold tracking-wider text-neutral-500">
                  {t("lineup")}
                  <span className="ml-2 text-neutral-600">
                    — {formatDayLong(selectedDay.date, locale)}
                  </span>
                </h2>
                <div className="flex items-center gap-2">
                  {showStageFilter && scheduleData && (
                    <StageFilter
                      stages={scheduleData.stages}
                      selected={activeSelected}
                      onToggle={handleStageToggle}
                      onSelectAll={() => setSelectedStages(null)}
                      onSelectNone={() => setSelectedStages(new Set())}
                      variant={festival.venue ? "stages" : "venues"}
                    />
                  )}
                  {viewMode === "my-grid" && selectedDay && (
                    <ExportGridButton
                      festivalName={festival.name}
                      dateLabel={formatDayLong(selectedDay.date, locale)}
                      day={selectedDay}
                      country={city.country}
                    />
                  )}
                  {isAdmin && (
                    <>
                      <button
                        onClick={() => setAddLineupOpen(true)}
                        className="flex h-7 w-7 items-center justify-center rounded bg-neutral-800 text-neutral-400 transition-all hover:text-acid hover:bg-neutral-700"
                        title="Add to lineup"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M12 5v14M5 12h14" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setAdminModalOpen(true)}
                        className="flex h-7 w-7 items-center justify-center rounded bg-neutral-800 text-neutral-400 transition-all hover:text-acid hover:bg-neutral-700"
                        title="Edit day event"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                          <path d="m15 5 4 4" />
                        </svg>
                      </button>
                    </>
                  )}
                </div>
              </div>
              <ScheduleView
                day={selectedDay}
                blockedSpotifyIds={blockedSpotifyIds}
                festivalId={festival.id}
                showOnlyMySchedule={viewMode === "my-grid"}
                stageVenues={stageVenues}
                visibleStages={visibleStages}
              />
            </div>
          ) : (
            /* List view — two-column layout */
            <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
              {/* Left column: Image + details + description */}
              <div>
                {festival.image_url && (
                  <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-neutral-800 mb-6">
                    <Image
                      src={festival.image_url}
                      alt={festival.name}
                      fill
                      className="object-cover"
                      sizes="(max-width: 1024px) 100vw, 50vw"
                      priority
                    />
                  </div>
                )}

                {/* Festival details card */}
                <div className="mb-6 space-y-3 rounded-lg border border-neutral-800 bg-surface p-5">
                  {festival.venue && (
                    <div className="flex items-start gap-3">
                      <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-neutral-500" />
                      <div>
                        <Link
                          href={`/${city.slug}/${festival.venue.slug}`}
                          className="font-mono text-sm text-white transition-colors hover:text-acid"
                        >
                          {festival.venue.name}
                        </Link>
                        {festival.venue.address && (
                          <p className="mt-0.5 text-xs text-neutral-500">
                            {festival.venue.address}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <Calendar className="h-4 w-4 shrink-0 text-neutral-500" />
                    <span className="font-mono text-sm text-neutral-300">
                      {formatDateRange(festival.start_date, festival.end_date, locale)}
                    </span>
                  </div>
                  {totalArtists > 0 && (
                    <div className="flex items-center gap-3">
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center font-mono text-xs text-neutral-500">
                        ♫
                      </span>
                      <span className="font-mono text-sm text-neutral-300">
                        {t("artists", { count: totalArtists })} · {days.length} {days.length === 1 ? "day" : "days"}
                      </span>
                    </div>
                  )}
                </div>

                {/* Tickets button */}
                {festival.url && (
                  <a
                    href={festival.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mb-6 flex w-full items-center justify-center gap-2 rounded-lg bg-acid py-4 font-mono text-sm font-bold tracking-wider text-black transition-all hover:bg-acid/90 hover:shadow-[0_0_30px_rgba(209,248,5,0.3)]"
                  >
                    {t("getTickets")}
                    <ExternalLink className="h-4 w-4" />
                  </a>
                )}

                {/* Description */}
                {festival.description && (
                  <div>
                    <h2 className="mb-3 font-mono text-xs font-bold tracking-wider text-neutral-500">
                      {t("about")}
                    </h2>
                    <div className="whitespace-pre-line text-sm leading-relaxed text-neutral-300">
                      {festival.description}
                    </div>
                  </div>
                )}
              </div>

              {/* Right column: Lineup */}
              <div>
                {/* Lineup section header */}
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-mono text-xs font-bold tracking-wider text-neutral-500">
                    {t("lineup")}
                    {selectedDay && (
                      <span className="ml-2 text-neutral-600">
                        — {formatDayLong(selectedDay.date, locale)}
                      </span>
                    )}
                  </h2>
                  <div className="flex items-center gap-2">
                    {showStageFilter && scheduleData && (
                      <StageFilter
                        stages={scheduleData.stages}
                        selected={activeSelected}
                        onToggle={handleStageToggle}
                        onSelectAll={() => setSelectedStages(null)}
                        onSelectNone={() => setSelectedStages(new Set())}
                        variant={festival.venue ? "stages" : "venues"}
                      />
                    )}
                    {isAdmin && selectedDay && (
                      <>
                        <button
                          onClick={() => setAddLineupOpen(true)}
                          className="flex h-7 w-7 items-center justify-center rounded bg-neutral-800 text-neutral-400 transition-all hover:text-acid hover:bg-neutral-700"
                          title="Add to lineup"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 5v14M5 12h14" />
                          </svg>
                        </button>
                        <button
                          onClick={() => setAdminModalOpen(true)}
                          className="flex h-7 w-7 items-center justify-center rounded bg-neutral-800 text-neutral-400 transition-all hover:text-acid hover:bg-neutral-700"
                          title="Edit day event"
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                            <path d="m15 5 4 4" />
                          </svg>
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {/* Day content */}
                {selectedDay ? (
                  <DayContent
                    day={selectedDay}
                    blockedSpotifyIds={blockedSpotifyIds}
                    visibleStages={showStageFilter ? activeSelected : undefined}
                  />
                ) : (
                  <p className="py-12 text-center font-mono text-sm text-neutral-500">
                    No lineup data available.
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Admin edit modal for selected day */}
      {adminModalOpen && selectedDay && (
        <EventAdminModal
          event={dayToEvent(selectedDay, festival)}
          onClose={() => setAdminModalOpen(false)}
        />
      )}

      {/* Add to lineup modal */}
      {addLineupOpen && selectedDay && (
        <AddLineupEntryModal
          eventId={selectedDay.event_id}
          existingStages={[...new Set(selectedDay.attractions.map((a) => a.stage).filter(Boolean) as string[])]}
          onClose={() => setAddLineupOpen(false)}
        />
      )}

      {/* Admin edit modal for festival */}
      {festivalAdminOpen && (
        <FestivalAdminModal
          festival={festival}
          onClose={() => setFestivalAdminOpen(false)}
        />
      )}

      {/* Jukebox modal — all days */}
      {jukeboxOpen && jukeboxPlaylist.length > 0 && (
        <GenreJukeboxModal
          playlist={jukeboxPlaylist}
          genreName={festival.name}
          selectedGenres={[]}
          citySlug={city.slug}
          titleOverride={tJukebox("festivalTitle")}
          // eslint-disable-next-line react-hooks/refs
          initialIndex={getJukeboxResumeIndex()}
          onClose={handleJukeboxClose}
          festivalId={festival.id}
        />
      )}
    </>
  );
}

export function FestivalPageClient({
  initialUser = null,
  ...rest
}: FestivalPageClientProps) {
  return (
    <AuthProvider initialUser={initialUser}>
      <ScheduleProvider festivalId={rest.festival.id} festivalName={rest.festival.name}>
        <FestivalPageInner {...rest} />
      </ScheduleProvider>
    </AuthProvider>
  );
}
